import { Map } from 'immutable'
import { createSelector } from '@reduxjs/toolkit'
import { get as getConfig } from '../../../../../config/index'
import type { Avatar } from '../../../types'
import {
  isStringMap,
  selectAuthUid,
  selectProfilePictureForUid,
  selectProfilePicturesData,
} from './state-types'

const config = getConfig()
const communityProfileEnabled = !!(config && config.features
  && config.features.communityProfile)

export const EMPTY_PICTURE: Avatar = { url: '', small: '', card: '' }

// Cache .toJS() conversions keyed on the Immutable object itself.
// WeakMap allows GC to reclaim entries when the Immutable object is no longer referenced,
// preventing unbounded memory growth as users are viewed.
const _toJsCache = new WeakMap<Map<string, unknown>, Avatar>()

function toAvatar (gql: Map<string, unknown>): Avatar {
  const cached = _toJsCache.get(gql)
  if (cached) return cached
  // .toJS() returns the wide JS union; the schema guarantees Avatar shape.
  const result = gql.toJS() as Avatar
  _toJsCache.set(gql, result)
  return result
}

/**
 * Select a profile picture for a given uid from the GraphQL lookup table.
 *
 * Feature-flag gated:
 *   ON  → returns { url, small, card } (empty strings if not yet loaded)
 *   OFF → returns null (callers use their existing REST paths)
 */
export function selectProfilePicture (
  state: { communityProfile: Map<string, unknown> },
  uid: string | number | null,
): Avatar | null {
  if (!communityProfileEnabled) return null
  if (!uid) return EMPTY_PICTURE
  const gql = selectProfilePictureForUid(state, String(uid))
  if (gql && gql.size > 0) return toAvatar(gql)
  return EMPTY_PICTURE
}

/**
 * Memoized selector for the current user's profile picture.
 * Uses createSelector so the derived .toJS() object is referentially stable
 * across renders when the underlying Immutable data hasn't changed.
 */
export const selectCurrentUserProfilePicture = createSelector(
  [selectAuthUid, selectProfilePicturesData],
  (uid, data): Avatar | null => {
    if (!communityProfileEnabled) return null
    if (!uid) return EMPTY_PICTURE
    const value = data.get(String(uid))
    if (isStringMap(value) && value.size > 0) return toAvatar(value)
    return EMPTY_PICTURE
  },
)

/**
 * Select the entire profilePictures lookup Map.
 *
 * Feature-flag gated:
 *   ON  → returns Immutable Map keyed by uid string
 *   OFF → returns empty Map (callers fall back to REST paths)
 */
export function selectProfilePicturesMap (
  state: { communityProfile: Map<string, unknown> },
): Map<string, unknown> {
  if (!communityProfileEnabled) return Map<string, unknown>()
  return selectProfilePicturesData(state)
}
